"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Phone, PhoneCall, Menu, X, ShieldAlert, ChevronRight, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
function BrandLogo({
  variant = "light",
  iconOnly = false,
  className = ""
}: {
  variant?: "light" | "dark";
  iconOnly?: boolean;
  className?: string;
}) {
  const dark = variant === "dark";
  return <span className={"inline-flex items-center gap-3 " + className}>
      <svg width="46" height="46" viewBox="0 0 64 64" aria-hidden="true" className="shrink-0">
        <defs>
          <linearGradient id="hdrTop" x1="10" y1="14" x2="54" y2="40" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#2A4A7C" />
            <stop offset="1" stopColor="#17304F" />
          </linearGradient>
          <linearGradient id="hdrLeft" x1="10" y1="26" x2="32" y2="58" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#10203A" />
            <stop offset="1" stopColor="#070F1D" />
          </linearGradient>
          <linearGradient id="hdrRight" x1="54" y1="26" x2="32" y2="58" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#1B3558" />
            <stop offset="1" stopColor="#0C1B30" />
          </linearGradient>
          <linearGradient id="hdrDrop" x1="24" y1="4" x2="42" y2="30" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FFD37A" />
            <stop offset="0.5" stopColor="#F6B63C" />
            <stop offset="1" stopColor="#D8860C" />
          </linearGradient>
          <radialGradient id="hdrShine" cx="0" cy="0" r="1" gradientTransform="translate(27 14) rotate(58) scale(9 6)" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
        </defs>
        <ellipse cx="32" cy="59" rx="19" ry="3.4" fill="#000000" opacity="0.18" />
        <path d="M32 20 54 32.5 32 45 10 32.5Z" fill={"url(#hdrTop)"} />
        <path d="M10 32.5 32 45v12.5L10 45Z" fill={"url(#hdrLeft)"} />
        <path d="M54 32.5 32 45v12.5L54 45Z" fill={"url(#hdrRight)"} />
        <path d="M32 20 54 32.5 32 45 10 32.5Z" fill="none" stroke="#3C6197" strokeOpacity="0.55" strokeWidth="0.9" />
        <path d="M32 45v12.5" stroke="#000000" strokeOpacity="0.35" strokeWidth="0.9" />
        <path d="M22 32.5 32 38.2 42 32.5" fill="none" stroke="#F6B63C" strokeOpacity="0.9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M32 3c0 0 11 12.4 11 19a11 11 0 0 1-22 0C21 15.4 32 3 32 3Z" fill={"url(#hdrDrop)"} />
        <path d="M32 3c0 0 11 12.4 11 19a11 11 0 0 1-22 0C21 15.4 32 3 32 3Z" fill="none" stroke="#B8700A" strokeOpacity="0.45" strokeWidth="0.8" />
        <path d="M32 3c0 0 11 12.4 11 19a11 11 0 0 1-22 0C21 15.4 32 3 32 3Z" fill={"url(#hdrShine)"} />
        <path d="M27.5 24.5a4.5 4.5 0 0 0 4.5 4.5" fill="none" stroke="#ffffff" strokeOpacity="0.75" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      {!iconOnly && <span className="flex flex-col leading-none">
          <span className={"text-[19px] font-bold tracking-tight " + (dark ? "text-white" : "text-slate-900")}>Jonesboro</span>
          <span className={"mt-1 text-[9.5px] font-semibold uppercase tracking-[0.17em] " + (dark ? "text-amber-400" : "text-amber-600")}>Emergency Plumber</span>
        </span>}
    </span>;
}
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="h-8 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }

  // Recursive menu item renderer
  const renderNavItems = (items: MenuItem[]) => {
    return items.map((item, i) => {
      const hasChildren = Boolean(item.children && item.children.length > 0);
      return <NavigationMenuItem key={item.id || i} data-index={i}>
          {hasChildren ? <div className="relative group/nav">
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="px-3 py-2 text-sm font-medium text-foreground transition-colors hover:text-primary flex items-center gap-1">
                  {item.label}
                </Link>
              </NavigationMenuLink>
              <div className="absolute top-full left-0 hidden group-hover/nav:block bg-popover text-popover-foreground border border-border rounded-md shadow-lg p-2 min-w-[200px] z-50">
                <ul className="space-y-1">
                  {item.children?.map((child, childIdx) => <li key={child.id || childIdx} data-index={childIdx}>
                      <Link to={child.href || "#"} className="block px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted rounded-sm transition-colors">
                        {child.label}
                      </Link>
                    </li>)}
                </ul>
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="px-3 py-2 text-sm font-medium text-foreground transition-colors hover:text-primary">
                {item.label}
              </Link>
            </NavigationMenuLink>}
        </NavigationMenuItem>;
    });
  };
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-0.5">
        {renderNavItems(menuItems)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3 p-4">
        {Array.from({
        length: 7
      }).map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  const renderMobileItems = (items: MenuItem[], depth = 0) => {
    return <ul className={depth > 0 ? "pl-4 space-y-1 mt-1 border-l border-border" : "space-y-1"}>
        {items.map((item, i) => {
        const hasChildren = Boolean(item.children && item.children.length > 0);
        return <li key={item.id || i} data-index={i}>
              <Link to={item.href || "#"} onClick={onNavigate} className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${depth === 0 ? "text-foreground hover:bg-muted hover:text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground text-xs"}`}>
                <span>{item.label}</span>
                {depth === 0 && <ChevronRight className="size-4 text-muted-foreground" />}
              </Link>
              {hasChildren && item.children && renderMobileItems(item.children, depth + 1)}
            </li>;
      })}
      </ul>;
  };
  return <div className="flex flex-col h-full justify-between py-2">
      <div className="overflow-y-auto max-h-[55vh] px-2">
        {renderMobileItems(menuItems)}
      </div>

      <div className="mt-4 pt-4 border-t border-border space-y-2 px-2">
        <a href="tel:4708479076" className="flex items-center justify-center gap-2 w-full rounded-full bg-secondary text-secondary-foreground py-3 text-sm font-bold shadow-xs hover:bg-secondary/80 transition-colors">
          <Phone className="size-4 text-primary" />
          <span>(470) 847-9076</span>
        </a>
        <Button asChild className="w-full rounded-full bg-accent text-accent-foreground font-bold hover:brightness-105 shadow-md py-3 text-sm">
          <Link to="/contact/" onClick={onNavigate}>
            <ShieldAlert className="size-4 mr-1.5" />
            Request Emergency Service
          </Link>
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Smooth scroll handler for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" data-nav="light" className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-md border-b border-border shadow-xs transition-all">
      {/* Top Urgent Status Bar */}
      <div className="bg-primary text-primary-foreground text-xs py-1.5 px-4">
        <div className="max-w-[1240px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 rounded-full bg-accent animate-pulse" />
            <span className="font-semibold tracking-wide">24/7 EMERGENCY DISPATCH ACTIVE IN JONESBORO, GA</span>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-primary-foreground/90 font-medium">
            <span className="flex items-center gap-1">
              <Clock className="size-3.5" /> 24/7 Availability
            </span>
            <span>Upfront Pricing</span>
            <span className="text-accent font-bold">Fast Local Response</span>
          </div>
        </div>
      </div>

      {/* Main Header Bar */}
      <div className="max-w-[1240px] mx-auto px-4 md:px-8 h-18 flex items-center justify-between gap-4">
        {/* Left: Brand Logo (wrapped in homepage link, no duplicate text) */}
        <div className="flex items-center shrink-0">
          <Link to="/" className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
            <BrandLogo className="" />
          </Link>
        </div>

        {/* Center: Desktop Navigation Menu */}
        <div className="hidden lg:flex items-center justify-center flex-1">
          <MenuProvider menu_id="16">
            <DesktopNavConsumer />
          </MenuProvider>
        </div>

        {/* Right Action Cluster */}
        <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">
          {/* Direct Phone Call Button */}
          <Button asChild variant="outline" className="hidden sm:inline-flex rounded-full border-border bg-card text-foreground hover:bg-secondary hover:text-secondary-foreground font-semibold px-3.5 h-10 shadow-xs">
            <a href="tel:4708479076" className="flex items-center gap-2">
              <span className="flex items-center justify-center size-7 rounded-full bg-secondary text-primary">
                <Phone className="size-3.5 text-primary" />
              </span>
              <span className="text-sm font-bold tracking-tight text-foreground">(470) 847-9076</span>
            </a>
          </Button>

          {/* Emergency Service Gold CTA */}
          <Button asChild className="rounded-full bg-accent text-accent-foreground font-bold hover:brightness-105 shadow-md hover:shadow-lg transition-[filter,box-shadow] h-10 px-4 sm:px-5">
            <Link to="/contact/" className="flex items-center gap-1.5">
              <PhoneCall className="size-4" />
              <span>Emergency Service</span>
            </Link>
          </Button>

          {/* Mobile Menu Drawer Trigger */}
          <div className="lg:hidden">
            <SafeHydrate><Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen} direction="right">
              <DrawerTrigger asChild>
                <Button variant="outline" size="icon" className="rounded-lg size-10 border-border bg-background text-foreground" aria-label="Open Navigation Menu">
                  <Menu className="size-5" />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="p-4 bg-background">
                <DrawerHeader className="p-0 pb-3 flex flex-row items-center justify-between border-b border-border">
                  <DrawerTitle className="text-left font-bold text-base flex items-center gap-2">
                    <span className="size-2 rounded-full bg-accent" />
                    <span>Navigation Menu</span>
                  </DrawerTitle>
                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon" className="size-8 rounded-full">
                      <X className="size-4" />
                    </Button>
                  </DrawerClose>
                </DrawerHeader>

                <div className="mt-2 flex-1">
                  <MenuProvider menu_id="16">
                    <MobileNavConsumer onNavigate={() => setIsDrawerOpen(false)} />
                  </MenuProvider>
                </div>
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>
        </div>
      </div>
    </section>;
}